/**
 * PSTUDIO-1433: decide whether the Expanded Cell View panel must SEED the
 * character the user typed on the grid cell (type-to-edit) into its own editor,
 * and with what value.
 *
 * Why the panel and not the grid: a JSON-capable column (richAssetContent /
 * parameterInput / analyticsEvent) NEVER mounts an inline <input>/<textarea>
 * (cell.tsx `isViewWhileSelected`) — it is edited exclusively in the panel's
 * JSON editor. `startCellEditingWithChar`'s rAF injection therefore has no
 * control to type into on these columns and used to drop the character
 * silently; instead the store parks it in `pendingInitialChar` (addressed to a
 * cell via `pendingInitialCharCellId`) and the panel applies it.
 *
 * The seed is ADDRESSED on purpose: a character whose panel never opened (a
 * later auto-open on a DIFFERENT cell) must be ignored, never fired into an
 * unrelated cell. Sheets semantics — the typed character REPLACES the cell
 * content, so the caller passes the return value straight to the editor's
 * change handler.
 */
import { isJsonCapableColumn } from './is-json-capable-column';

export function resolveInitialCharSeed(params: {
  /** tableStore.pendingInitialChar — the character typed on the grid cell. */
  pendingChar: string | null;
  /** tableStore.pendingInitialCharCellId — `${rowId}-${columnId}` it targets. */
  pendingCharCellId: string | null;
  /** Column the panel currently edits. */
  columnId: string | undefined;
  /** `${rowId}-${columnId}` the panel currently edits. */
  cellId: string | null;
}): string | null {
  const { pendingChar, pendingCharCellId, columnId, cellId } = params;
  // No pending character (or an empty one — nothing to type).
  if (!pendingChar) return null;
  // Only the JSON-capable columns route their first character through the
  // panel; every other column is seeded by the store's own rAF injection into
  // the inline control, and seeding here too would double-apply it.
  if (!isJsonCapableColumn(columnId)) return null;
  // The character belongs to a specific cell — drop it otherwise.
  if (!cellId || pendingCharCellId !== cellId) return null;
  return pendingChar;
}

/**
 * PSTUDIO-1433: the panel's per-auto-open seed slot. `char` is the character to
 * apply (null = nothing to apply for this nonce); `applied` makes the write
 * one-shot even if the holder is read again.
 */
export type NonceSeed = {
  nonce: number;
  char: string | null;
  applied: boolean;
};

/**
 * PSTUDIO-1433: resolve the seed slot for an autofocus nonce, consuming the
 * store's pending character AT MOST ONCE per nonce.
 *
 * Why this is a separate step rather than a plain read inside the panel's
 * effect: React 18 replays an effect's cleanup+body without a new nonce
 * (StrictMode double-invokes effects on mount; a remount does the same). The
 * store read is destructive — `startCellEditingWithChar` parks the character and
 * the panel clears it — so a read living in the effect's closure loses the
 * character on the replayed run, and it is the REPLAYED run whose rAF loop
 * survives to apply it. Verified live (2026-08-25, PR #2701 manual test): the
 * panel effect fired twice under one nonce and the typed character never reached
 * the editor. Holding the consumed value against its nonce makes the replay
 * read the SAME seed while keeping the store consumption one-shot per keystroke.
 *
 * `read` is called ONLY when the nonce is new, so the caller can clear the store
 * inside it without risking a second clear.
 */
export function resolveSeedForNonce(
  current: NonceSeed | null,
  nonce: number,
  read: () => string | null
): NonceSeed {
  if (current?.nonce === nonce) return current;
  return { nonce, char: read(), applied: false };
}
